/**
 * Application-wide constants for Generation 5 battle simulator.
 */

/** The Pokemon generation this simulator targets. */
export const GENERATION = 5 as const;

/** Showdown format identifier for Gen 5. */
export const FORMAT = "gen5" as const;

/** Maximum number of Pokemon on a team. */
export const MAX_TEAM_SIZE = 6;

/** Minimum number of Pokemon required for a battle. */
export const MIN_TEAM_SIZE = 1;

/** Maximum total EVs across all stats. */
export const MAX_EVS = 510;

/** Maximum EVs per single stat. */
export const MAX_EV_PER_STAT = 252;

/** Maximum IV per stat. */
export const MAX_IV = 31;

/** Default level for battles. */
export const DEFAULT_LEVEL = 50;

/** Maximum level. */
export const MAX_LEVEL = 100;

/** Default turn timer in seconds. */
export const DEFAULT_TURN_TIMER = 60;

/** Cookie key prefixes for persistent storage. */
export const COOKIE_KEYS = {
  THEME: "pbs-theme",
  TRAINER: "pbs-trainer",
  LAST_TEAM: "pbs-last-team",
  CUSTOM_THEME: "pbs-custom-theme",
} as const;

/** IndexedDB database name. */
export const DB_NAME = "pokemon-battle-sim";

/** IndexedDB store names. */
export const DB_STORES = {
  TEAMS: "teams",
  SPRITES: "sprites",
  REPLAYS: "replays",
} as const;

/** Trystero room code prefix. */
export const ROOM_PREFIX = "pbs-b2w2-";

/** Application version sent in P2P HELLO messages. */
export const APP_VERSION = "0.2.3";

/** Sprite size limits in pixels. */
export const SPRITE_LIMITS = {
  POKEMON_FRONT: 96,
  POKEMON_BACK: 80,
  TRAINER_BACK: 160,
  TRAINER_FRONT: 96,
  VS: 128,
} as const;

/** Sprite format preference order, from most to least preferred. */
export const SPRITE_FORMAT_ORDER = ["gif", "apng", "png", "jpg", "jpeg"] as const;

/** Interval between raw frame sequence cycles in milliseconds. */
export const SPRITE_FRAME_INTERVAL_MS = 120;

/** Maximum number of raw frame files to probe for a single sprite. */
export const MAX_RAW_FRAMES = 60;

/** Media query string for reduced motion preference. */
export const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/** Fallback CSS color for battle backgrounds when no image is available. */
export const BATTLE_BACKGROUND_FALLBACK = "#1a1a2e";

/** Heartbeat interval in milliseconds during P2P battles. */
export const PING_INTERVAL_MS = 5000;

/** Time without a PONG before considering the peer disconnected. */
export const PONG_TIMEOUT_MS = 15000;

/** Time to wait for a peer to reconnect before giving up. */
export const RECONNECT_TIMEOUT_MS = 60000;

/**
 * Optional local signaling relay URL for Trystero.
 * Set VITE_RELAY_URL for a self-hosted bittorrent-tracker for local/LAN testing.
 * In production (no env var), this is empty and only public trackers are used.
 * Both peers iterate over the tracker list in the same deterministic order,
 * so they converge on the same tracker even if some are unreachable.
 */
export const RELAY_URL = import.meta.env.VITE_RELAY_URL ?? "";

/** WebSocket signaling server URL for the last-resort fallback relay. */
export const SIGNALING_URL = import.meta.env.VITE_SIGNALING_URL ?? "";

/** STUN servers used for public IP discovery during WebRTC negotiation. */
export const STUN_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

/** Explicit STUN-only RTC configuration. No TURN servers are configured to preserve strict P2P gameplay. */
export const RTC_CONFIG: RTCConfiguration = {
  iceServers: STUN_SERVERS,
  iceTransportPolicy: "all",
  iceCandidatePoolSize: 10,
};

/**
 * Public BitTorrent/WebSocket tracker fallbacks for production builds.
 * These are tried in order for peer discovery via Trystero's torrent strategy.
 * All peers iterate this list in the same order, so they converge on the same
 * tracker even if some are unreachable.
 */
export const PUBLIC_RELAY_URLS = [
  "wss://tracker.openwebtorrent.com",
  "wss://tracker.files.fm:7073/announce",
  "wss://tracker.btorrent.xyz",
];

